// Per-round pick window state for the golf pick'em mode.
// PURE — takes pairings + clock, returns the round's window state.
//
// The pick'em mode runs FOUR independent pick windows per pool (one
// per round). Each round's window opens when its pairings publish in
// field-updates and locks PER-PAIRING at each pairing's tee time.
// Sparse picks are fine: a user can pick some rounds and not others.
//
// Why this lives here: the rule is "lock per-pairing at teeTime"; the
// adapter already exposes teeTime on every grouping (contract extension
// from C2). Computing window state from pairings + clock is the same
// kind of pure transformation as matchup grading. Drops into the mode
// UI (round tabs + per-pairing locked badge) and the picks-save path
// (server-side guard against late writes — though client-side is the
// primary UX gate).
//
// ── Memorial verification target (HANDOFF Sequencing C2) ─────────────
// The R3 pick window depends on POST-CUT timing — when DataGolf adds
// R3 pairings to field-updates relative to Saturday tee-off. The
// Memorial Friday capture surfaces this; until then, the state machine
// is correct in the abstract (lock by teeTime), but the operational
// "is the window ever workable?" gate stays open.

const VALID_STATES = new Set([
  'not-published',   // round's pairings absent from feed → no window yet
  'open',            // pairings present, no pairing has hit its teeTime
  'partial-locked', // some pairings locked, others still open
  'fully-locked',   // all pairings have hit teeTime (round closed to picks)
])

/**
 * Parse DataGolf's "YYYY-MM-DD HH:MM" teeTime string to a UTC ms
 * timestamp, adjusted by the tournament's tz_offset (hours east of
 * UTC; negative for the Americas).
 *
 * teeTime is in tournament-local time. For prod accuracy the caller
 * MUST pass tzOffsetHours from the field-updates response. Without
 * it, the function falls back to treating the string as UTC — fine
 * for lexical tests but inaccurate against real clocks.
 *
 * @param {string|null|undefined} teeTime
 * @param {number} [tzOffsetHours]
 * @returns {number|null} ms-epoch, or null on malformed input
 */
export function parseTeeTimeMs(teeTime, tzOffsetHours = 0) {
  if (!teeTime) return null
  const m = String(teeTime).match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/)
  if (!m) return null
  const utcAsLocal = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  // tz_offset is hours east of UTC. To convert local→UTC: subtract offset.
  // EDT = -4 → UTC = local - (-4 * 3600 * 1000) = local + 4h ✓
  return utcAsLocal - tzOffsetHours * 3600 * 1000
}

/**
 * Is this specific pairing locked at the given clock?
 *
 * @param {string|null|undefined} teeTime  DataGolf "YYYY-MM-DD HH:MM"
 * @param {number} nowMs                   ms-epoch
 * @param {number} [tzOffsetHours]
 * @returns {boolean}  True = locked (user CANNOT pick); false = open
 */
export function isPairingLocked(teeTime, nowMs, tzOffsetHours = 0) {
  const t = parseTeeTimeMs(teeTime, tzOffsetHours)
  // Malformed teeTime → treat as locked. Conservative: better to refuse
  // a pick than let one through against an unknown lock time. Caller
  // can surface a "teeTime missing" error to operator.
  if (t == null) return true
  return nowMs >= t
}

/**
 * Compute the round's pick-window state plus per-pairing summary.
 *
 * @param {object} input
 * @param {number} input.round                1-4
 * @param {Array<{groupId: string, teeTime: string}>} input.pairings
 *   Pairings for ONE round (caller filters by round).
 * @param {number|Date|string} input.now
 *   Current clock (ms-epoch, Date, or ISO string).
 * @param {number} [input.tzOffsetHours]
 *   Tournament's tz_offset from field-updates response (hours east of UTC).
 * @returns {{
 *   round: number,
 *   state: 'not-published' | 'open' | 'partial-locked' | 'fully-locked',
 *   totalPairings: number,
 *   openPairings: number,
 *   lockedPairings: number,
 *   nextLockMs: number | null,   // ms-epoch of the next pairing to lock, or null
 *   lockedGroupIds: string[],    // groupIds currently locked
 * }}
 */
export function windowState({ round, pairings, now, tzOffsetHours = 0 }) {
  const nowMs = (typeof now === 'number') ? now
              : (now instanceof Date)     ? now.getTime()
              : new Date(now).getTime()

  if (!Array.isArray(pairings) || pairings.length === 0) {
    return {
      round,
      state:           'not-published',
      totalPairings:   0,
      openPairings:    0,
      lockedPairings:  0,
      nextLockMs:      null,
      lockedGroupIds:  [],
    }
  }

  let openCount = 0
  let lockedCount = 0
  let nextLockMs = null
  const lockedGroupIds = []

  for (const p of pairings) {
    const t = parseTeeTimeMs(p.teeTime, tzOffsetHours)
    if (t == null) {
      // Malformed teeTime is locked (conservative). Not counted in
      // nextLockMs since "next" implies future.
      lockedCount++
      lockedGroupIds.push(p.groupId)
      continue
    }
    if (nowMs >= t) {
      lockedCount++
      lockedGroupIds.push(p.groupId)
    } else {
      openCount++
      if (nextLockMs == null || t < nextLockMs) nextLockMs = t
    }
  }

  let state
  if (openCount > 0 && lockedCount > 0)  state = 'partial-locked'
  else if (lockedCount > 0)              state = 'fully-locked'
  else                                   state = 'open'

  return {
    round,
    state,
    totalPairings:  pairings.length,
    openPairings:   openCount,
    lockedPairings: lockedCount,
    nextLockMs,
    lockedGroupIds,
  }
}

/**
 * Compute window states for ALL four rounds. Convenience wrapper for
 * the UI's round-tab strip (each tab shows a badge derived from state).
 *
 * @param {object} input
 * @param {{[round: number]: Array<{groupId: string, teeTime: string}>}} input.pairingsByRound
 * @param {number|Date|string} input.now
 * @param {number} [input.tzOffsetHours]
 * @returns {Array<ReturnType<typeof windowState>>}  Length 4 (R1-R4).
 */
export function allRoundWindows({ pairingsByRound, now, tzOffsetHours = 0 }) {
  const out = []
  for (let r = 1; r <= 4; r++) {
    out.push(windowState({
      round:     r,
      pairings:  pairingsByRound?.[r] || [],
      now,
      tzOffsetHours,
    }))
  }
  return out
}

/**
 * Whether a round is "in progress" — at least one pairing has locked AND
 * no picks have graded yet. Drives:
 *   - live-scores polling in GolfPicksMode (poll only when true)
 *   - the live-vs-finals render decision (combined with shouldShowLiveScores
 *     below for the per-pairing leakage gate)
 *
 * Truth table:
 *   roundState     roundPicks state              isRoundInProgress
 *   not-published  any                           false
 *   open           any                           false
 *   partial-locked empty                         false
 *   partial-locked every pick.result===pending   TRUE
 *   partial-locked any pick.result≠pending       false
 *   fully-locked   empty                         false
 *   fully-locked   every pick.result===pending   TRUE
 *   fully-locked   any pick.result≠pending       false
 *
 * The "any graded → false" branch is what makes the polling stop the
 * moment the grader's gate releases: as soon as one pick transitions
 * non-pending, isRoundInProgress flips to false → effect cleanup runs
 * → liveScores cleared → render falls back to finals from pick.roundScores.
 *
 * @param {object} args
 * @param {'not-published'|'open'|'partial-locked'|'fully-locked'} args.roundState
 * @param {Array<{result?: string}>} args.roundPicks  Saved picks for this round.
 * @returns {boolean}
 */
export function isRoundInProgress({ roundState, roundPicks }) {
  if (roundState !== 'fully-locked' && roundState !== 'partial-locked') return false
  if (!Array.isArray(roundPicks) || roundPicks.length === 0) return false
  return roundPicks.every(p => (p?.result || 'pending') === 'pending')
}

/**
 * Per-pairing gate: should this pairing's live (provisional) scores render?
 *
 * Three load-bearing conditions, all required:
 *   1. hasFinals === false      — finals take precedence the moment they land
 *   2. isInProgress === true    — round must be in progress (no polling otherwise)
 *   3. pairingLocked === true   — pairing must be locked by tee time
 *
 * Condition 3 is the information-leakage gate. The round-level poll runs
 * across all 49 pairings, but showing live opponent scores on a still-
 * pickable pairing would let the user read in-progress scoring before
 * committing their pick. Lock-by-teeTime is exactly the boundary that
 * already gates pick-acceptance — same boundary gates render here.
 *
 * @param {object} args
 * @param {boolean} args.isInProgress    isRoundInProgress's return value
 * @param {boolean} args.pairingLocked   isPairingLocked(teeTime, now, tz)
 * @param {boolean} args.hasFinals       pick.roundScores != null
 * @returns {boolean}
 */
export function shouldShowLiveScores({ isInProgress, pairingLocked, hasFinals }) {
  if (hasFinals)     return false
  if (!isInProgress) return false
  return !!pairingLocked
}

export { VALID_STATES }
